/**
 * The local database — IndexedDB via Dexie, and the single source of truth for
 * everything the UI renders. Writes land here first and return instantly;
 * Supabase is a mirror that catches up later. The front desk never waits for
 * the network.
 *
 * Two IndexedDB constraints shape this schema:
 *
 *  1. Booleans are not indexable. `dirty` is queried on every sync push, so it
 *     is stored as 0 | 1. `cancelled` and `active` stay booleans — they are
 *     only ever read, never used as a query index.
 *
 *  2. `null` is not indexable either, so `deleted_at` cannot be an index.
 *     Soft-deleted rows are filtered in JS by the `live()` helpers below.
 *     At gym scale (thousands of rows) this is not worth optimising around.
 *
 * No unique indexes on synced tables: a unique constraint that throws in the
 * middle of a pull would wedge the sync engine. Uniqueness for `sk_id`,
 * `class_key` and branch `name` is enforced in application code here and by a
 * unique index in Postgres, where a violation can be handled properly.
 */
import Dexie, { type Table } from 'dexie';
import type {
  AppSetting,
  Coach,
  CoachAssignment,
  Freeze,
  Member,
  Payment,
  PriceRow,
  Setting,
  Subscription,
  SyncLogEntry,
  WalkIn,
} from '@shared/types';
import { nowTimestamp } from '@shared/dates';

export class SkDatabase extends Dexie {
  members!: Table<Member, string>;
  subscriptions!: Table<Subscription, string>;
  payments!: Table<Payment, string>;
  price_schedule!: Table<PriceRow, string>;
  coach_assignments!: Table<CoachAssignment, string>;
  coaches!: Table<Coach, string>;
  app_settings!: Table<AppSetting, string>;
  freezes!: Table<Freeze, string>;
  walk_ins!: Table<WalkIn, string>;
  settings!: Table<Setting, string>;
  sync_log!: Table<SyncLogEntry, number>;

  constructor(name = 'sk_frontdesk') {
    super(name);

    this.version(1).stores({
      members: 'id, sk_id, phone, full_name, branch, updated_at, dirty',
      subscriptions:
        'id, member_id, class_key, branch, end_date, start_date, updated_at, dirty, [member_id+start_date]',
      payments:
        'id, subscription_id, member_id, paid_at, updated_at, dirty, [subscription_id+paid_at]',
      class_prices: 'id, class_key, updated_at, dirty',
      branches: 'id, name, updated_at, dirty',
      settings: 'key',
      sync_log: '++seq, started_at, status',
    });

    /**
     * v2 — the academy runs a single location, so branches were removed
     * entirely: the `branches` store is dropped and the `branch` index is gone
     * from members and subscriptions.
     *
     * v1 is kept above rather than rewritten so a machine that already opened
     * the earlier build upgrades cleanly instead of failing to open. Dexie
     * deletes a store when its schema is set to null.
     */
    this.version(2)
      .stores({
        members: 'id, sk_id, phone, full_name, updated_at, dirty',
        // [member_id+start_date] powers the member profile's history, newest first.
        subscriptions:
          'id, member_id, class_key, end_date, start_date, updated_at, dirty, [member_id+start_date]',
        payments:
          'id, subscription_id, member_id, paid_at, updated_at, dirty, [subscription_id+paid_at]',
        class_prices: 'id, class_key, updated_at, dirty',
        // Drop-in sessions. Separate from subscriptions — see WalkIn in types.ts.
        walk_ins: 'id, class_key, visited_at, updated_at, dirty',
        branches: null,
        // Local only — never synced. PIN hash, sync cursors, clock offset.
        settings: 'key',
        // Local only — one row per sync attempt, for the Settings debug view.
        sync_log: '++seq, started_at, status',
      })
      .upgrade(async (tx) => {
        // Drop the now-meaningless branch field from any rows created by the
        // earlier build, so nothing carries a stale value up to Supabase.
        await tx
          .table('members')
          .toCollection()
          .modify((m: Record<string, unknown>) => {
            delete m.branch;
          });
        await tx
          .table('subscriptions')
          .toCollection()
          .modify((s: Record<string, unknown>) => {
            delete s.branch;
          });
      });

    /**
     * v3 — the real City View price schedule.
     *
     * Prices are now TOTALS for the term (1/3/6 months) and vary by club-house
     * membership and group/private, so the old per-month `class_prices` table
     * cannot express them. It is replaced by `price_schedule`, a flat 24-row
     * lookup. `coach_assignments` arrives at the same time to drive payouts.
     */
    this.version(3)
      .stores({
        members: 'id, sk_id, phone, full_name, updated_at, dirty',
        subscriptions:
          'id, member_id, class_key, end_date, start_date, updated_at, dirty, [member_id+start_date]',
        payments:
          'id, subscription_id, member_id, paid_at, updated_at, dirty, [subscription_id+paid_at]',
        price_schedule: 'id, price_key, updated_at, dirty',
        coach_assignments: 'id, class_key, coach_name, updated_at, dirty',
        walk_ins: 'id, class_key, visited_at, updated_at, dirty',
        class_prices: null,
        settings: 'key',
        sync_log: '++seq, started_at, status',
      })
      .upgrade(async (tx) => {
        // Existing members predate club-house pricing; default them to
        // non-member, which is the higher rate and therefore the safe guess.
        await tx
          .table('members')
          .toCollection()
          .modify((m: Record<string, unknown>) => {
            if (m.club_member === undefined) m.club_member = false;
          });

        // Convert old per-month subscriptions to the term-total shape. The
        // amount actually charged is preserved exactly — total_due_piastres was
        // always the real figure, so history stays truthful.
        await tx
          .table('subscriptions')
          .toCollection()
          .modify((s: Record<string, unknown>) => {
            if (s.price_piastres === undefined) {
              const perMonth = Number(s.price_per_month_piastres ?? 0);
              const months = Number(s.months ?? 1);
              s.price_piastres = Number(s.total_due_piastres ?? perMonth * months);
            }
            delete s.price_per_month_piastres;
            if (s.private === undefined) s.private = false;
            if (s.club_member === undefined) s.club_member = false;
          });
      });

    /**
     * v4 — subscription freezes.
     *
     * A freeze pushes the subscription's end_date out, so no existing query
     * changes. These rows record how the date got there, and let a freeze be
     * cancelled by pulling the same number of days back off.
     */
    this.version(4).stores({
      members: 'id, sk_id, phone, full_name, updated_at, dirty',
      subscriptions:
        'id, member_id, class_key, end_date, start_date, updated_at, dirty, [member_id+start_date]',
      payments:
        'id, subscription_id, member_id, paid_at, updated_at, dirty, [subscription_id+paid_at]',
      price_schedule: 'id, price_key, updated_at, dirty',
      coach_assignments: 'id, class_key, coach_name, updated_at, dirty',
      freezes: 'id, subscription_id, member_id, start_date, end_date, updated_at, dirty',
      walk_ins: 'id, class_key, visited_at, updated_at, dirty',
      settings: 'key',
      sync_log: '++seq, started_at, status',
    });

    /**
     * v5 — shared settings.
     *
     * The walk-in price, the payout split and the gym name move out of the
     * device-local `settings` store into `app_settings`, which syncs. That is
     * what lets a manager change a price in the portal and have the front desk
     * pick it up. Device-specific things (PIN hashes, sync cursors, the clock
     * offset, the SK ID counter) stay in `settings` and never leave the machine.
     */
    this.version(5)
      .stores({
        members: 'id, sk_id, phone, full_name, updated_at, dirty',
        subscriptions:
          'id, member_id, class_key, end_date, start_date, updated_at, dirty, [member_id+start_date]',
        payments:
          'id, subscription_id, member_id, paid_at, updated_at, dirty, [subscription_id+paid_at]',
        price_schedule: 'id, price_key, updated_at, dirty',
        coach_assignments: 'id, class_key, coach_name, updated_at, dirty',
        app_settings: 'id, key, updated_at, dirty',
        freezes: 'id, subscription_id, member_id, start_date, end_date, updated_at, dirty',
        walk_ins: 'id, class_key, visited_at, updated_at, dirty',
        settings: 'key',
        sync_log: '++seq, started_at, status',
      })
      .upgrade(async (tx) => {
        // Carry any values the owner already changed across to the synced
        // table, so upgrading does not silently reset a price.
        const shared = [
          'walk_in_price_piastres',
          'coach_percent',
          'manager_percent',
          'city_view_percent',
          'gym_name',
        ];
        const local = tx.table('settings');
        const target = tx.table('app_settings');
        const ts = new Date().toISOString();

        for (const key of shared) {
          const row = (await local.get(key)) as { value?: string } | undefined;
          if (!row?.value) continue;
          if (await target.where('key').equals(key).first()) continue;
          await target.add({
            id: crypto.randomUUID(),
            key,
            value: row.value,
            created_at: ts,
            updated_at: ts,
            deleted_at: null,
            dirty: 1,
          });
        }
      });

    /**
     * v6 — private-training coaches and cancellations.
     *
     * `coaches` is a roster that is NOT tied to a class, for the people who
     * only run private sessions and therefore have no class to be assigned to.
     *
     * The subscription gains the coach who runs its privates, plus the record
     * of a cancellation. The refund itself is a negative row in `payments`, not
     * a field here — that table is append-only and already feeds every revenue
     * figure, so a refund reaches the portals without any report changing.
     */
    this.version(6)
      .stores({
        members: 'id, sk_id, phone, full_name, updated_at, dirty',
        subscriptions:
          'id, member_id, class_key, end_date, start_date, updated_at, dirty, [member_id+start_date]',
        payments:
          'id, subscription_id, member_id, paid_at, updated_at, dirty, [subscription_id+paid_at]',
        price_schedule: 'id, price_key, updated_at, dirty',
        coach_assignments: 'id, class_key, coach_name, updated_at, dirty',
        coaches: 'id, name, updated_at, dirty',
        app_settings: 'id, key, updated_at, dirty',
        freezes: 'id, subscription_id, member_id, start_date, end_date, updated_at, dirty',
        walk_ins: 'id, class_key, visited_at, updated_at, dirty',
        settings: 'key',
        sync_log: '++seq, started_at, status',
      })
      .upgrade(async (tx) => {
        // Rows written before this release have no coach and no cancellation
        // record. Fill them in rather than leaving `undefined`, which would be
        // pushed to Postgres as a missing column and rejected by the NOT NULL
        // on private_coach.
        //
        // These rows are deliberately NOT marked dirty: nothing about them
        // changed, and re-pushing every subscription on upgrade would flood the
        // first sync after an update with rows the server already has.
        await tx
          .table('subscriptions')
          .toCollection()
          .modify((s: Record<string, unknown>) => {
            if (s.private_coach === undefined) s.private_coach = '';
            if (s.cancelled_at === undefined) s.cancelled_at = null;
            if (s.sessions_attended === undefined) s.sessions_attended = null;
            if (s.refund_piastres === undefined) s.refund_piastres = null;
          });
      });
  }
}

export const db = new SkDatabase();

/* -------------------------------------------------------------------------
   Row helpers
   ------------------------------------------------------------------------- */

/**
 * The audit fields every synced row is born with. UUIDs are generated on the
 * client so a row created offline already has its final identity — no
 * server round trip, and sync upserts are naturally idempotent.
 *
 * Requires a secure context (HTTPS or localhost) for crypto.randomUUID.
 */
export function newRowFields(): {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: null;
  dirty: 1;
} {
  const ts = nowTimestamp();
  return { id: crypto.randomUUID(), created_at: ts, updated_at: ts, deleted_at: null, dirty: 1 };
}

/** Mark a row as locally modified so the next sync push picks it up. */
export function touch<T extends { updated_at: string; dirty: 0 | 1 }>(row: T): T {
  row.updated_at = nowTimestamp();
  row.dirty = 1;
  return row;
}

/** Soft delete. Rows are never removed — every query filters `deleted_at`. */
export function markDeleted<T extends { deleted_at: string | null; updated_at: string; dirty: 0 | 1 }>(
  row: T
): T {
  row.deleted_at = nowTimestamp();
  row.updated_at = nowTimestamp();
  row.dirty = 1;
  return row;
}

/** True for rows that have not been soft-deleted. */
export function isLive<T extends { deleted_at: string | null }>(row: T): boolean {
  return row.deleted_at === null;
}

/** Filter a list down to live rows. The `deleted_at IS NULL` of every query. */
export function live<T extends { deleted_at: string | null }>(rows: T[]): T[] {
  return rows.filter(isLive);
}

/* -------------------------------------------------------------------------
   Settings (local, key/value)
   ------------------------------------------------------------------------- */

export const SETTING_KEYS = {
  pinHash: 'pin_hash',
  pinSalt: 'pin_salt',
  /** A SECOND PIN, for Settings. The front desk staff are not given this one. */
  managerPinHash: 'manager_pin_hash',
  managerPinSalt: 'manager_pin_salt',
  /** Failed-attempt state per PIN kind. Local only, so a reload cannot reset it. */
  pinThrottleStaff: 'pin_throttle_staff',
  pinThrottleManager: 'pin_throttle_manager',
  gymName: 'gym_name',
  coachPercent: 'coach_percent',
  managerPercent: 'manager_percent',
  cityViewPercent: 'city_view_percent',
  currency: 'currency',
  skIdCounter: 'sk_id_counter',
  walkInPrice: 'walk_in_price_piastres',
  clockOffsetMs: 'clock_offset_ms',
  lastSyncedAt: 'last_synced_at',
  syncCursorPrefix: 'sync_cursor_',
  seededAt: 'seeded_at',
} as const;

export async function getSetting(key: string): Promise<string | null> {
  const row = await db.settings.get(key);
  return row?.value ?? null;
}

export async function setSetting(key: string, value: string): Promise<void> {
  await db.settings.put({ key, value, updated_at: nowTimestamp() });
}

export async function getSettingNumber(key: string, fallback: number): Promise<number> {
  const raw = await getSetting(key);
  if (raw === null) return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

/* -------------------------------------------------------------------------
   Shared settings (synced) — prices, the payout split, the gym name
   ------------------------------------------------------------------------- */

/** Keys that live in the SYNCED table, so the portal can change them too. */
export const SHARED_SETTING_KEYS = {
  walkInPrice: 'walk_in_price_piastres',
  coachPercent: 'coach_percent',
  managerPercent: 'manager_percent',
  cityViewPercent: 'city_view_percent',
  /**
   * JSON: coach name -> their own percentage. Exceptions only; everyone absent
   * is on `coach_percent`. A blob rather than a column because the roster lives
   * in two tables — class coaches in `coach_assignments`, private-only coaches
   * in `coaches` — and a name is the only key both share.
   */
  coachRates: 'coach_rates',
  gymName: 'gym_name',
} as const;

export async function getSharedSetting(key: string): Promise<string | null> {
  const row = await db.app_settings.where('key').equals(key).first();
  return row && row.deleted_at === null ? row.value : null;
}

export async function getSharedSettingNumber(key: string, fallback: number): Promise<number> {
  const raw = await getSharedSetting(key);
  if (raw === null) return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

export async function setSharedSetting(key: string, value: string): Promise<void> {
  const existing = await db.app_settings.where('key').equals(key).first();
  if (existing) {
    existing.value = value;
    await db.app_settings.put(touch(existing));
  } else {
    await db.app_settings.add({ ...newRowFields(), key, value });
  }
}
